import { useEffect } from 'react';
import { HEADER_BUTTON_MODE } from '@hqo/hqo-miniapp-client-sdk';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { getLaunchUrl } from 'app/shared/hooks/use-launch-url';

/**
 * Drives the host header's left button: a close (X) on the initial screen, but a back button
 * that returns to the miniapp once the user is deep in a Mindbody flow.
 *
 * We can't observe the widget's cross-origin navigation, and the header mode we set while loaded
 * persists into whatever page the webview flat-navigates to (we lose control once gone). So we
 * flip at the moment of leaving: start with a close button, and when the page is hidden/unloaded
 * — the booking/auth flow navigating the webview away — switch to history mode targeting the
 * launch URL, giving the user a way back. Returning to our own page restores the close button.
 */
export const HeaderNavManager = (): null => {
  const sdk = useMiniappSdk();

  useEffect(() => {
    const setCloseButton = () => sdk.header.setHeader(undefined, HEADER_BUTTON_MODE.CLOSE);

    const setReturnButton = () => {
      const launchUrl = getLaunchUrl();

      if (launchUrl) {
        sdk.header.setHeader(undefined, HEADER_BUTTON_MODE.HISTORY, launchUrl, 'fas chevron-left');
      }
    };

    // Initial screen: close button.
    setCloseButton();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setReturnButton();
      } else {
        setCloseButton();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', setReturnButton);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', setReturnButton);
    };
  }, [sdk]);

  return null;
};
